/**
 * P3-5 财务工作台（admin / director-finance）
 *
 * 3 统计卡 + 待开发票表格
 * item3②：发票图改为「员工在发票卡直传」，财务工作台只看员工上传的图 + 通过(POST /invoices/:id/open 无需带图)/退回。
 */
import { useState } from 'react';
import { Modal, Input, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { PageHeader, HomeCard, Btn, Pill, StatCard, TableWrap, invTypeLabel } from '../../components/ui';
// ux9 日期：统一用 lib/format 出口
import { fmtDateTime } from '../../lib/format';

export function FinanceWorkbenchPage() {
  const qc = useQueryClient();
  const [opening, setOpening] = useState<any>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const { data } = useQuery<any>({
    queryKey: ['finance-dashboard'],
    queryFn: () => $.get('/invoice-workflow/finance-dashboard'),
    refetchInterval: 30_000,
  });

  const openInv = useMutation({
    // item3②：财务只「通过」，不再传图——后端用员工已直传的 inv.imageUrl 开票
    mutationFn: (id: number) => $.post(`/invoices/${id}/open`, {}),
    onSuccess: () => {
      message.success('已开发票，已通知销售员');
      setOpening(null);
      qc.invalidateQueries({ queryKey: ['finance-dashboard'] });
    },
    onError: (e: any) => message.error(e?.message ?? '开票失败'),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      $.post(`/invoices/${id}/reject`, { reason }),
    onSuccess: () => {
      message.success('已退回');
      setRejectingId(null); setRejectReason('');
      qc.invalidateQueries({ queryKey: ['finance-dashboard'] });
    },
    onError: (e: any) => message.error(e?.message ?? '退回失败'),
  });

  const stats = data?.stats ?? {};
  const pending: any[] = data?.pending ?? [];

  return (
    <>
      <PageHeader
        breadcrumb={['财务', '发票工作台']}
        title="📋 发票工作台"
        subtitle="销售员申请 → 程华庆开 → 销售员发给客户 → 客户确认"
      />
      <div className="work-content">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 16 }}>
          <StatCard label="待开发票" value={stats.pendingOpen ?? 0} unit="张"
                    valueColor={stats.pendingOpen > 0 ? 'warning' : undefined} />
          <StatCard label="已开未发" value={stats.openedNotSent ?? 0} unit="张"
                    valueColor={stats.openedNotSent > 0 ? 'primary' : undefined} />
          <StatCard label="已发未确认" value={stats.sentNotConfirmed ?? 0} unit="张" />
        </div>

        <HomeCard title={`待开发票（${pending.length}）`}>
          <TableWrap>
            <table className="ck-table" style={{ width: '100%', minWidth: 820 }}>
              <thead>
                <tr>
                  <th>申请人</th>
                  <th>客户</th>
                  <th style={{ textAlign: 'right' }}>金额</th>
                  <th>类型</th>
                  <th>抬头</th>
                  <th>税号</th>
                  <th>申请时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {pending.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--success)' }}>
                    ✅ 当前没有待开发票
                  </td></tr>
                )}
                {pending.map((inv: any) => (
                  <tr key={inv.id}>
                    <td>{inv.requestedByName ?? `#${inv.requestedByUserId}`}</td>
                    <td>{inv.customerName ?? `#${inv.customerId}`}</td>
                    <td className="money" style={{ textAlign: 'right' }}>¥{Number(inv.amount).toLocaleString()}</td>
                    <td><Pill tone={inv.type === 'invoice' ? 'primary' : 'default'}>{invTypeLabel(inv.type)}</Pill></td>
                    <td>{inv.headerName}</td>
                    <td>{inv.taxNo ?? '—'}</td>
                    <td>{fmtDateTime(inv.requestedAt)}</td>
                    <td style={{ display: 'flex', gap: 6 }}>
                      <Btn kind="primary" onClick={() => setOpening(inv)}>开发票</Btn>
                      <Btn kind="default" onClick={() => setRejectingId(inv.id)}>退回</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </HomeCard>
      </div>

      <Modal
        title={`开发票 - ${opening?.headerName ?? ''}`}
        open={!!opening}
        onCancel={() => { if (!openInv.isPending) setOpening(null); }}
        footer={
          <>
            <Btn kind="default" onClick={() => setOpening(null)} disabled={openInv.isPending}>取消</Btn>
            <Btn kind="primary"
                 onClick={() => openInv.mutate(opening.id)}
                 disabled={!opening?.imageUrl || openInv.isPending}>
              {openInv.isPending ? '提交中…' : '通过开票'}
            </Btn>
          </>
        }
      >
        {/* item3②：财务只审核——展示员工已直传的发票图，确认无误点「通过开票」；图缺则只能退回催传 */}
        <div style={{ marginBottom: 12 }}>
          {opening?.imageUrl ? (
            <>
              <div style={{ marginBottom: 8, fontSize: 13, color: 'var(--text-2)' }}>申请人上传的发票 / 收据图，请核对：</div>
              <img src={opening.imageUrl} alt="发票图"
                   style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 6, border: '1px solid var(--border)' }} />
            </>
          ) : (
            <div style={{ padding: 12, background: 'var(--warning-bg, #fff7e6)', borderRadius: 4, fontSize: 13, color: 'var(--warning, #b7791f)' }}>
              ⚠️ 申请人尚未上传发票图，无法开票。请「退回」并提示其到客户详情 → 发票卡上传开好的发票图。
            </div>
          )}
        </div>
        {opening && (
          <div style={{ padding: 10, background: 'var(--bg-2)', borderRadius: 4, fontSize: 12 }}>
            <div>客户：{opening.customerName ?? `#${opening.customerId}`}</div>
            <div>抬头：{opening.headerName}</div>
            {opening.taxNo && <div>税号：{opening.taxNo}</div>}
            <div>金额：¥{Number(opening.amount).toLocaleString()}</div>
            <div>类型：{invTypeLabel(opening.type)}</div>
          </div>
        )}
      </Modal>

      <Modal
        title="退回发票申请"
        open={!!rejectingId}
        onCancel={() => { if (!reject.isPending) { setRejectingId(null); setRejectReason(''); } }}
        footer={
          <>
            <Btn kind="default" onClick={() => { setRejectingId(null); setRejectReason(''); }} disabled={reject.isPending}>取消</Btn>
            <Btn kind="primary"
                 onClick={() => rejectingId && reject.mutate({ id: rejectingId, reason: rejectReason })}
                 disabled={!rejectReason || reject.isPending}>
              {reject.isPending ? '提交中…' : '确认退回'}
            </Btn>
          </>
        }
      >
        <Input.TextArea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                        rows={4} placeholder="如：抬头错误 / 税号缺失 / 金额对不上 ..." />
      </Modal>
    </>
  );
}
